import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import cls from "./UserMemberPartnerRelation.module.scss";
import api from "@/utils/axios";
import { timeAgo } from "@/utils/timeAgo";
import {
  RELATIONSHIP_TYPES,
  RELATIONSHIP_STATUSES,
} from "@/enums/PartnerEnums";

const UserMemberPartnerRelation = () => {
  const navigate = useNavigate();
  const { userId, partnerId } = useParams();

  const [relation, setRelation] = useState(null);
  const [memberId, setMemberId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [primaryLoading, setPrimaryLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [recoverLoading, setRecoverLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const [formState, setFormState] = useState({
    relationshipType: "",
    relationshipStatus: "",
    linkedUserId: "",
  });

  const formatLabel = (value) => {
    if (!value) return "";
    return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  };

  const loadRelation = async () => {
    if (!userId || !partnerId) return;
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const res = await api.get(
        `/v1/admin/members/single/partners-info/relation/${userId}/${partnerId}`,
      );
      const r = res.data?.relation;
      setRelation(r || null);
      setMemberId(res.data?.memberId || null);

      setFormState({
        relationshipType: r?.relationshipType || "",
        relationshipStatus: r?.relationshipStatus || "",
        linkedUserId: r?.userId || "",
      });
    } catch (err) {
      console.error("Error loading partner relation:", err);
      setError("Failed to load partner relation info.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRelation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, partnerId]);

  const handleBack = () => {
    navigate(
      `/admin/members/single/partners-info/single/${userId}/${partnerId}`,
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!relation) return;
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      setDeleteConfirm(false);

      const payload = {
        user: formState.linkedUserId || null,
        relationshipType: formState.relationshipType || null,
        relationshipStatus: formState.relationshipStatus || null,
      };

      const res = await api.patch(
        `/v1/admin/members/single/partners-info/relation/${userId}/${partnerId}`,
        payload,
      );

      const updated = res.data?.relation;
      if (updated) {
        setRelation(updated);
      }
      setSuccess(res.data?.message || "Relation updated.");
    } catch (err) {
      console.error("Error updating relation:", err);
      setError(
        err?.response?.data?.message || "Failed to update partner relation.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMakePrimary = async () => {
    if (!relation) return;
    try {
      setPrimaryLoading(true);
      setError("");
      setSuccess("");
      setDeleteConfirm(false);

      const res = await api.patch(
        `/v1/admin/members/single/partners-info/make-primary/${userId}/${partnerId}`,
      );

      const updated = res.data?.relation;
      if (updated) {
        setRelation(updated);
      }
      setSuccess(res.data?.message || "Partner set as primary.");
    } catch (err) {
      console.error("Error making partner primary:", err);
      setError(
        err?.response?.data?.message || "Failed to make partner primary.",
      );
    } finally {
      setPrimaryLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      setSuccess("");
      setError("");
      return;
    }
  };

  const handleConfirmDelete = async () => {
    if (!relation) return;
    try {
      setDeleteLoading(true);
      setError("");
      setSuccess("");

      const res = await api.patch(
        `/v1/admin/members/single/partners-info/delete/${userId}/${partnerId}`,
      );

      const updated = res.data?.relation;
      if (updated) {
        setRelation(updated);
      }
      setSuccess(res.data?.message || "Partner deleted.");
    } catch (err) {
      console.error("Error deleting partner:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to delete partner. Check rules.",
      );
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm(false);
  };

  const handleRecover = async () => {
    if (!relation) return;
    try {
      setRecoverLoading(true);
      setError("");
      setSuccess("");
      setDeleteConfirm(false);

      const res = await api.patch(
        `/v1/admin/members/single/partners-info/recover/${userId}/${partnerId}`,
      );

      const updated = res.data?.relation;
      if (updated) {
        setRelation(updated);
      }
      setSuccess(res.data?.message || "Partner recovered.");
    } catch (err) {
      console.error("Error recovering partner:", err);
      setError(err?.response?.data?.message || "Failed to recover partner.");
    } finally {
      setRecoverLoading(false);
    }
  };

  const computed = useMemo(() => {
    if (!relation) {
      return {
        isPrimary: false,
        isDeleted: false,
        statusBadge: "",
        statusLabel: "",
      };
    }
    const isPrimary = !!relation.isPrimary;
    const isDeleted = !!relation.isDeleted;

    let statusBadge = "";
    let statusLabel = "";
    if (isDeleted) {
      statusBadge = "deleted";
      statusLabel = "Deleted";
    } else if (isPrimary) {
      statusBadge = "primary";
      statusLabel = "Primary";
    } else {
      statusBadge = "active";
      statusLabel = "Active";
    }

    return { isPrimary, isDeleted, statusBadge, statusLabel };
  }, [relation]);

  if (loading) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back to Partner Profile
          </button>
          <div className={cls.titleBlock}>
            <h1 className={cls.title}>Partner Relation</h1>
            <p className={cls.subtitle}>Loading relation info…</p>
          </div>
        </div>

        <div className={cls.loadingCard}>
          <span className={cls.spinner} />
          <span>Fetching relation from the Universe…</span>
        </div>
      </div>
    );
  }

  if (error && !relation) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back to Partner Profile
          </button>
          <div className={cls.titleBlock}>
            <h1 className={cls.title}>Partner Relation</h1>
            <p className={cls.subtitle}>Something went wrong.</p>
          </div>
        </div>

        <div className={cls.errorCard}>
          <p>{error || "Relation not found."}</p>
        </div>
      </div>
    );
  }

  if (!relation) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back to Partner Profile
          </button>
          <div className={cls.titleBlock}>
            <h1 className={cls.title}>Partner Relation</h1>
            <p className={cls.subtitle}>No relation data available.</p>
          </div>
        </div>

        <div className={cls.errorCard}>
          <p>Relation not found.</p>
        </div>
      </div>
    );
  }

  const { isPrimary, isDeleted, statusBadge, statusLabel } = computed;

  const relationshipTypeOptions = RELATIONSHIP_TYPES || [];
  const relationshipStatusOptions = RELATIONSHIP_STATUSES || [];

  return (
    <div className={cls.page}>
      {/* Header */}
      <div className={cls.headerRow}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back to Partner Profile
        </button>

        <div className={cls.titleBlock}>
          <h1 className={cls.title}>Partner Relation</h1>
          <p className={cls.subtitle}>
            Manage the relationship type, status, and admin flags for this
            partner.
          </p>
        </div>
      </div>

      {/* Messages */}
      {(error || success) && (
        <div className={cls.messageRow}>
          {error && <div className={cls.errorBanner}>{error}</div>}
          {success && !error && (
            <div className={cls.successBanner}>{success}</div>
          )}
        </div>
      )}

      {/* Meta strip */}
      <section className={cls.metaStrip}>
        <div className={cls.metaLeft}>
          <div className={cls.metaLabel}>Relation Context</div>
          <div className={cls.metaValues}>
            <span className={cls.metaChip}>
              User ID:
              <span className={cls.metaChipValue}>{userId}</span>
            </span>
            {memberId && (
              <span className={cls.metaChip}>
                Member ID:
                <span className={cls.metaChipValue}>{memberId}</span>
              </span>
            )}
            <span className={cls.metaChip}>
              Partner ID:
              <span className={cls.metaChipValue}>{relation.id}</span>
            </span>
            {relation.userId && (
              <span className={cls.metaChip}>
                Linked User:
                <span className={cls.metaChipValue}>{relation.userId}</span>
              </span>
            )}
          </div>
        </div>
        <div className={cls.metaRight}>
          <span
            className={`${cls.statusBadge} ${
              statusBadge === "primary"
                ? cls.statusBadgePrimary
                : statusBadge === "deleted"
                  ? cls.statusBadgeDeleted
                  : cls.statusBadgeActive
            }`}
          >
            {statusLabel}
          </span>
        </div>
      </section>

      {/* Main grid */}
      <div className={cls.grid}>
        {/* Left: editable relation fields */}
        <section className={cls.card}>
          <h2 className={cls.cardTitle}>Relation Details</h2>
          <p className={cls.cardSubtitle}>
            Update the relationship type and status. These define how this
            partner is connected to the member inside Noty Universe.
          </p>

          <form className={cls.form} onSubmit={handleSave}>
            <div className={cls.formRow}>
              <label className={cls.label} htmlFor="relationshipType">
                Relationship Type
              </label>
              <select
                id="relationshipType"
                name="relationshipType"
                className={cls.select}
                value={formState.relationshipType}
                onChange={handleInputChange}
              >
                <option value="">Not set</option>
                {relationshipTypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {formatLabel(t)}
                  </option>
                ))}
              </select>
              <p className={cls.helpText}>
                Example: Married, Engaged, Life Partners, etc.
              </p>
            </div>

            <div className={cls.formRow}>
              <label className={cls.label} htmlFor="relationshipStatus">
                Relationship Status
              </label>
              <select
                id="relationshipStatus"
                name="relationshipStatus"
                className={cls.select}
                value={formState.relationshipStatus}
                onChange={handleInputChange}
              >
                <option value="">Not set</option>
                {relationshipStatusOptions.map((s) => (
                  <option key={s} value={s}>
                    {formatLabel(s)}
                  </option>
                ))}
              </select>
              <p className={cls.helpText}>
                Example: Active &amp; Secure, Exploring, On a Break, etc.
              </p>
            </div>

            <div className={cls.formRow}>
              <label className={cls.label} htmlFor="linkedUserId">
                Linked User ID (optional)
              </label>
              <input
                id="linkedUserId"
                name="linkedUserId"
                className={cls.input}
                type="text"
                value={formState.linkedUserId}
                onChange={handleInputChange}
                placeholder="Paste another userId to link, or leave blank"
              />
              <p className={cls.helpText}>
                Used only if this partner should also exist as a full login
                user.
              </p>
            </div>

            <div className={cls.formActions}>
              <button type="submit" className={cls.saveBtn} disabled={saving}>
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </form>
        </section>

        {/* Right: status, primary, delete/recover */}
        <section className={cls.card}>
          <h2 className={cls.cardTitle}>Admin Controls</h2>
          <p className={cls.cardSubtitle}>
            Manage primary status and lifecycle of this partner. These actions
            affect member type and primary partner rules.
          </p>

          <div className={cls.flagRows}>
            <div className={cls.flagRow}>
              <span className={cls.flagLabel}>Primary Partner</span>
              <span
                className={`${cls.flagValue} ${
                  isPrimary ? cls.flagValueOn : cls.flagValueOff
                }`}
              >
                {isPrimary ? "Yes" : "No"}
              </span>
            </div>
            <div className={cls.flagRow}>
              <span className={cls.flagLabel}>Deleted</span>
              <span
                className={`${cls.flagValue} ${
                  isDeleted ? cls.flagValueDeleted : cls.flagValueActive
                }`}
              >
                {isDeleted ? "Deleted" : "Active"}
              </span>
            </div>
          </div>

          <div className={cls.divider} />

          <div className={cls.adminButtons}>
            <button
              type="button"
              className={cls.primaryBtn}
              onClick={handleMakePrimary}
              disabled={primaryLoading || isDeleted}
            >
              {primaryLoading
                ? "Updating…"
                : isPrimary
                  ? "Already Primary"
                  : "Make Primary"}
            </button>

            {!isDeleted ? (
              <button
                type="button"
                className={cls.deleteBtn}
                onClick={handleDeleteClick}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Processing…" : "Delete Partner"}
              </button>
            ) : (
              <button
                type="button"
                className={cls.recoverBtn}
                onClick={handleRecover}
                disabled={recoverLoading}
              >
                {recoverLoading ? "Recovering…" : "Recover Partner"}
              </button>
            )}
          </div>

          {deleteConfirm && !isDeleted && (
            <div className={cls.deleteConfirmBox}>
              <p className={cls.deleteConfirmTitle}>
                Are you sure you want to delete this partner?
              </p>
              <p className={cls.deleteConfirmText}>
                This is a soft delete. Our rules will check if this is the only
                partner or the primary partner. You might be asked to select
                another primary before deletion.
              </p>
              <div className={cls.deleteConfirmActions}>
                <button
                  type="button"
                  className={cls.deleteConfirmYes}
                  onClick={handleConfirmDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Deleting…" : "Yes, Delete"}
                </button>
                <button
                  type="button"
                  className={cls.deleteConfirmNo}
                  onClick={handleCancelDelete}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className={cls.metaDates}>
            <div className={cls.metaDateItem}>
              <span className={cls.metaDateLabel}>Created</span>
              <span className={cls.metaDateValue}>
                {formatDate(relation.createdAt)}
                {relation.createdAt && (
                  <span className={cls.metaDateMuted}>
                    {" "}
                    ({timeAgo(relation.createdAt)})
                  </span>
                )}
              </span>
            </div>
            <div className={cls.metaDateItem}>
              <span className={cls.metaDateLabel}>Last Updated</span>
              <span className={cls.metaDateValue}>
                {formatDate(relation.updatedAt)}
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default UserMemberPartnerRelation;
